import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { TenantConfigContext } from 'context/TenantConfigContext';
import * as TopList from 'components/Layout/Tiles/ListContainer';
import { DataEntry } from 'models/data';
import { useContext, useState } from 'react';
import { buildApiImageUrl } from 'services/product-data.service';
import { Measure, useModelService } from 'services/model/model.service';
import { Pagination } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState } from 'components/EmptyState';

interface ProductsListProps {
  list: DataEntry[] | undefined;
  comparisonActive?: boolean;
  measurePrimary: Measure;
  measureSecondary: Measure;
  testId?: string;
}

export function ProductsList({
  list,
  comparisonActive = false,
  measurePrimary,
  measureSecondary,
  testId = 'top-performing-products',
}: ProductsListProps) {
  const { getAttributes } = useModelService();
  const navigate = useNavigate();
  const location = useLocation();

  const [prdName, prdNumber, artImageUrl] = getAttributes(['prdName', 'prdNumber', 'artImageUrl']);

  const [pageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const navigateToProductDetails = (productNumber: string) => {
    const url = `/product/${encodeURIComponent(productNumber)}${location.search}`;
    navigate(url);
  };

  const tenantContext = useContext(TenantConfigContext);
  if (!list) {
    return <MetricsLoadingSpinner />;
  }
  if (list.length === 0) {
    return <EmptyState />;
  }
  return (
    <div className="flex flex-col items-center justify-between h-full" data-testid={testId}>
      <TopList.ListContainer>
        {list.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(listItem => (
          <TopList.ListRow
            key={prdNumber.getValue(listItem.attributes)}
            data-testid="product-row"
            onClick={() => navigateToProductDetails(listItem.attributes.prdNumber)}
          >
            <TopList.NameCell
              name={prdName.getValue(listItem.attributes)}
              imgSrc={buildApiImageUrl(tenantContext.accountNumber, artImageUrl.getValue(listItem.attributes))}
              hideColorBar
              color={listItem.attributes.color}
            />
            <TopList.MetricCell
              measure={measurePrimary}
              value={measurePrimary.getValue(listItem.values)}
              comparisonValue={measurePrimary.getValue(listItem.compareValues)}
              unit={measurePrimary.metric.unit}
              comparisonActive={comparisonActive}
              size="lg"
            />
            <TopList.MetricCell
              measure={measureSecondary}
              value={measureSecondary.getValue(listItem.values)}
              comparisonValue={measureSecondary.getValue(listItem.compareValues)}
              unit={measureSecondary.metric.unit}
              comparisonActive={comparisonActive}
            />
          </TopList.ListRow>
        ))}
      </TopList.ListContainer>
      {Math.ceil(list.length / pageSize) > 1 && (
        <Pagination
          className="mt-4"
          size="small"
          count={Math.ceil(list.length / pageSize)}
          page={currentPage}
          onChange={(_event, page) => setCurrentPage(page)}
        />
      )}
    </div>
  );
}
